import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from "react-router-dom";
import { setupAuthInterceptor } from './utils/authInterceptor';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { addLocale, locale, PrimeReactProvider } from 'primereact/api';
import fr from "primelocale/fr.json";
import "primereact/resources/primereact.min.css";
import 'primeicons/primeicons.css';
import "primeflex/primeflex.css";
import "primereact/resources/themes/mdc-light-deeppurple/theme.css";
import { QueryClient, QueryClientProvider } from 'react-query';
import { MainLayout } from './layouts/MainLayout';
import ErrorPage from './error-page';
import { AuthLayout } from './layouts/AuthLayout';
import { Login } from './pages/auth/Login';
import { SchoolHome, loader as schoolLoader } from './pages/school/SchoolHome';
import SchoolSlotChangeRequestsPage from './pages/school/SchoolSlotChangeRequestsPage';
import SchoolStudentObservationsPage from './pages/school/SchoolStudentObservationsPage';
import { MembersListPage } from './pages/school/members/MembersListPage';
import { MemberDetailPage } from './pages/school/members/MemberDetailPage';
import { SchoolLayout } from './layouts/SchoolLayout';
import AboutPage from './pages/school/about/AboutPage';
import PermissionsPage from './pages/school/permissions/PermissionsPage';
import TrainingsListPage from './pages/trainings/TrainingsListPage';
import { TrainingsLayout, loader as trainingLoader } from './pages/trainings/TrainingsLayout';
import ClassroomsListPage from './pages/trainings/classrooms/ClassroomsListPage';
import SlotsListPage from './pages/trainings/slots/SlotsListPage';
import LevelsListPage from './pages/trainings/levels/LevelsListPage';
import CoursesListPage from './pages/trainings/courses/CoursesListPage';
import ClassesListPage from './pages/trainings/classes/ClassesListPage';
import TeachersListPage from './pages/trainings/teachers/TeachersListPage';
import ScheduleListPage from './pages/trainings/schedule/ScheduleListPage';
import EventsListPage from './pages/trainings/events/EventsListPage';
import EventDetailPage from './pages/trainings/events/EventDetailPage';
import SessionsListPage from './pages/trainings/sessions/SessionsListPage';
import SessionDetailPage from './pages/trainings/sessions/SessionDetailPage';
import GenerateSessionsPage from './pages/trainings/sessions/GenerateSessionsPage';
import TrainingImportPage from './pages/trainings/import/TrainingImportPage';
import TrainingInfoPage from './pages/trainings/info/TrainingInfoPage';
import DocumentsListPage from './pages/trainings/documents/DocumentsListPage';
import StudentsListPage from './pages/trainings/students/StudentsListPage';
import AttendancesListPage from './pages/trainings/attendances/AttendancesListPage';
import PlanningDetailPage from './pages/trainings/planning/PlanningDetailPage';
import { FamilyPage } from './pages/family/FamilyPage';
import RegistrationsListPage from './pages/registrations/RegistrationsListPage';
import RegistrationStatsPage from './pages/registrations/stats/RegistrationStatsPage';
import { RegistrationsLayout, loader as registrationLoader } from './pages/registrations/RegistrationsLayout';
import RegistrationSettingsPage from './pages/registrations/settings/RegistrationSettingsPage';
import RegistrationCampaignsPage from './pages/registrations/campaigns/RegistrationCampaignsPage';
import RegistrationPaymentsPage from './pages/registrations/payments/RegistrationPaymentsPage';
import RegistrationNewCampaignPage from './pages/registrations/campaigns/RegistrationNewCampaignPage';
import ClassroomDetailPage from './pages/trainings/classrooms/ClassroomDetailPage';
import SlotDetailPage from './pages/trainings/slots/SlotDetailPage';
import LevelDetailPage from './pages/trainings/levels/LevelDetailPage';
import CourseDetailPage from './pages/trainings/courses/CourseDetailPage';
import ClasseDetailPage from './pages/trainings/classes/ClasseDetailPage';
import TeacherDetailPage from './pages/trainings/teachers/TeacherDetailPage';
import { UserLayout } from './layouts/UserLayout';
import UserProfileDetailPage from './pages/user/profile/UserProfileDetailPage';
import UserSchoolsDetailPage from './pages/user/schools/UserSchoolsDetailPage';
import { SchoolLayoutRedirects } from './pages/school/SchoolLayoutRedirects';
import { SystemLayout } from './layouts/SystemLayout';
import SystemAccountsListPage from './pages/system/accounts/SystemAccountsListPage';
import SystemSchoolsListPage from './pages/system/schools/SystemSchoolsListPage';
import SystemFeaturesListPage from './pages/system/features/SystemFeaturesListPage';
import StudentDetailPage from './pages/trainings/students/StudentDetailPage';
import SlotChangeRequestsListPage from './pages/trainings/slot-change-requests/SlotChangeRequestsListPage';
import SlotChangeProposalsPage from './pages/trainings/slot-change-requests/SlotChangeProposalsPage';
import CandidatesListPage from './pages/registrations/candidates/CandidatesListPage';
import CandidateDetailPage from './pages/registrations/candidates/CandidateDetailPage';
import ImportCandidatesFromTrainingPage from './pages/registrations/candidates/ImportCandidatesFromTrainingPage';
import PasswordReset from './pages/auth/PasswordReset';
import RegistrationCampaignsDetailPage from './pages/registrations/campaigns/RegistrationCampaignsDetailPage';
import LandingPage from './pages/landing/LandingPage';
import NewRegistrationPage from './pages/registrations/candidates/NewRegistrationPage';
import ImportRegistrationsFromExcelFilePage from './pages/registrations/candidates/ImportRegistrationsFromExcelFilePage';
import DoPasswordReset from './pages/auth/DoPasswordReset';
import OAuthCallback from './pages/auth/OAuthCallback';
import { SecretariatHome } from './pages/secretariat/SecretariatHome';
import SecretariatClassesListPage from './pages/secretariat/classes/SecretariatClassesListPage';
import SecretariatClassesDetailPage from './pages/secretariat/classes/SecretariatClassesDetailPage';
import SecretariatClasseDetailPage from './pages/secretariat/classes/SecretariatClasseDetailPage';
import PrintClassePage from './pages/secretariat/classes/PrintClassePage';
import PrintAllClassesPage from './pages/secretariat/classes/PrintAllClassesPage';
import PrintScheduleAssignmentsPage from './pages/trainings/schedule/PrintScheduleAssignmentsPage';
import PresencesListPage from './pages/secretariat/presences/PresencesListPage';
import PresencesDetailPage from './pages/secretariat/presences/PresencesDetailPage';
import PresencesSessionDetailPage from './pages/secretariat/presences/PresencesSessionDetailPage';
import SecretariatStudentsListPage from './pages/secretariat/students/SecretariatStudentsListPage';
import SecretariatStudentsDetailPage from './pages/secretariat/students/SecretariatStudentsDetailPage';
import PrintAllStudentsPage from './pages/secretariat/students/PrintAllStudentsPage';
import SecretariatPaymentsListPage from './pages/secretariat/payments/SecretariatPaymentsListPage';
import SecretariatPaymentsDetailPage from './pages/secretariat/payments/SecretariatPaymentsDetailPage';
import SecretariatSlotChangeRequestsListPage from './pages/secretariat/slot-change-requests/SecretariatSlotChangeRequestsListPage';
import SecretariatSlotChangeRequestsDetailPage from './pages/secretariat/slot-change-requests/SecretariatSlotChangeRequestsDetailPage';
import SecretariatTeachersListPage from './pages/secretariat/teachers/SecretariatTeachersListPage';
import PrintAllTeachersPage from './pages/secretariat/teachers/PrintAllTeachersPage';
import SecretariatAgendaListPage from './pages/secretariat/agenda/SecretariatAgendaListPage';
import SecretariatAgendaDetailPage from './pages/secretariat/agenda/SecretariatAgendaDetailPage';
import ExportRegistrationsPage from './pages/registrations/export/ExportRegistrationsPage';
import { FamilyRequestAccessPage } from './pages/family/FamilyRequestAccessPage';
import { FamilySchoolPage } from './pages/family/FamilySchoolPage';
import { FamilyTrainingPage } from './pages/family/FamilyTrainingPage';
import { HelloAssoReturnPage } from './pages/family/HelloAssoReturnPage';
import { HelloAssoErrorPage } from './pages/family/HelloAssoErrorPage';
import ContractDetailPage from './pages/registrations/contracts/ContractDetailPage';
import ContractsListPage from './pages/registrations/contracts/ContractsListPage';
import ContractsStatsPage from './pages/registrations/contracts/ContractsStatsPage';
import ContractsPaymentsSettingsPage from './pages/registrations/contracts/ContractsPaymentsSettingsPage';
import PrintAllContractsPage from './pages/registrations/contracts/PrintAllContractsPage';
import { TeacherHome } from './pages/teacher/TeacherHome';
import { TeacherAvisPassagePage } from './pages/teacher/TeacherAvisPassagePage';
import { TeacherTrainingsPage } from './pages/teacher/TeacherTrainingsPage';
import { TeacherClassesPage } from './pages/teacher/TeacherClassesPage';
import { TeacherClassesTrainingsPage } from './pages/teacher/TeacherClassesTrainingsPage';
import { TeacherClassesDetailPage } from './pages/teacher/TeacherClassesDetailPage';
import { TeacherClasseDetailPage } from './pages/teacher/TeacherClasseDetailPage';
import { TeacherSessionsTrainingsPage } from './pages/teacher/TeacherSessionsTrainingsPage';
import { TeacherSessionsPage } from './pages/teacher/TeacherSessionsPage';
import { TeacherAgendaPage } from './pages/teacher/TeacherAgendaPage';
import SchoolMailsPage from './pages/school/SchoolMailsPage';

setupAuthInterceptor();
addLocale("fr", fr);
locale("fr");

const configs = {
  locale: "fr",
  cssTransition: false,
};
const queryClient = new QueryClient();


const RoutesJSX = (
  <Route path="/" element={<MainLayout />} errorElement={<ErrorPage />}>
    <Route path="auth" element={<AuthLayout />}>
      <Route path="login" element={<Login />} />
      <Route path="resetpass" element={<PasswordReset />} />
      <Route path="resetpass/:token" element={<DoPasswordReset />} />
      <Route path="oauth/callback" element={<OAuthCallback />} />
    </Route>
    <Route path="welcome" element={<LandingPage />} />

    <Route path="user" element={<UserLayout />}>
      <Route path="profile" element={<UserProfileDetailPage />} />
      <Route path="schools" element={<UserSchoolsDetailPage />} />
    </Route>
    <Route path="school" element={<SchoolLayout />}>
      <Route path=":schoolId" element={<SchoolLayoutRedirects />}>
        <Route path="admin">
          <Route index element={<SchoolHome />} loader={schoolLoader} />
          <Route path="home" element={<SchoolHome />} loader={schoolLoader} />
          <Route path="slot-change-requests" element={<SchoolSlotChangeRequestsPage />} />
          <Route path="slot-change-requests/:trainingId" element={<SchoolSlotChangeRequestsPage />} />
          <Route path="slot-change-requests/:trainingId/proposals" element={<SchoolSlotChangeRequestsPage />} />
          <Route path="student-observations" element={<SchoolStudentObservationsPage />} />
          <Route path="student-observations/:trainingId" element={<SchoolStudentObservationsPage />} />
          <Route path="avis-passage" element={<TeacherTrainingsPage />} loader={schoolLoader} />
          <Route path="avis-passage/:trainingId" element={<TeacherClassesPage />} loader={schoolLoader} />
          <Route path="trainings/:trainingId/classes/:classId/avis-passage" element={<TeacherAvisPassagePage />} />
          <Route path="trainings" element={<TrainingsListPage />} />
          <Route path="trainings/:trainingId" element={<TrainingsLayout />} loader={trainingLoader}>
            <Route index element={<TrainingInfoPage />} />
            <Route path="info" element={<TrainingInfoPage />} />
            <Route path="classrooms" element={<ClassroomsListPage />} />
            <Route path="classrooms/:classroomId" element={<ClassroomDetailPage />} />
            <Route path="slots" element={<SlotsListPage />} />
            <Route path="slots/:slotId" element={<SlotDetailPage />} />
            <Route path="levels" element={<LevelsListPage />} />
            <Route path="levels/:levelId" element={<LevelDetailPage />} />
            <Route path="courses" element={<CoursesListPage />} />
            <Route path="courses/:courseId" element={<CourseDetailPage />} />
            <Route path="classes" element={<ClassesListPage />} />
            <Route path="classes/:classeId" element={<ClasseDetailPage />} loader={trainingLoader} />
            <Route path="teachers" element={<TeachersListPage />} loader={trainingLoader} />
            <Route path="teachers/print-all-teachers" element={<PrintAllTeachersPage />} loader={trainingLoader} />
            <Route path="teachers/:teacherId" element={<TeacherDetailPage />} loader={trainingLoader} />
            <Route path="schedule" element={<ScheduleListPage />} />
            <Route path="schedule/print-assignments" element={<PrintScheduleAssignmentsPage />} />
            <Route path="events" element={<EventsListPage />} />
            <Route path="events/:eventId" element={<EventDetailPage />} />
            <Route path="sessions" element={<SessionsListPage />} loader={trainingLoader} />
            <Route path="sessions/:sessionId" element={<SessionDetailPage />} loader={trainingLoader} />
            <Route path="generate-sessions" element={<GenerateSessionsPage />} loader={trainingLoader} />
            <Route path="import" element={<TrainingImportPage />} loader={trainingLoader} />
            <Route path="documents" element={<DocumentsListPage />} loader={trainingLoader} />
            <Route path="students" element={<StudentsListPage />} loader={trainingLoader} />
            <Route path="students/:studentId" element={<StudentDetailPage />} loader={trainingLoader} />
            <Route path="slot-change-requests/proposals" element={<SlotChangeProposalsPage />} loader={trainingLoader} />
            <Route path="slot-change-requests" element={<SlotChangeRequestsListPage />} loader={trainingLoader} />
            <Route path="attendances" element={<AttendancesListPage />} loader={trainingLoader} />
            <Route path="planning" element={<PlanningDetailPage />} loader={trainingLoader} />
          </Route>
          <Route path="registrations" element={<RegistrationsListPage />} />
          <Route path="registrations/:registrationId" element={<RegistrationsLayout />} loader={registrationLoader}>
            <Route index element={<RegistrationSettingsPage />} loader={registrationLoader} />
            <Route path="stats" element={<RegistrationStatsPage />} />
            <Route path="settings" element={<RegistrationSettingsPage />} loader={registrationLoader} />
            <Route path="campaigns" element={<RegistrationCampaignsPage />} />
            <Route path="campaigns-new" element={<RegistrationNewCampaignPage />} loader={registrationLoader} />
            <Route path="campaigns/:campaignId" element={<RegistrationCampaignsDetailPage />} loader={registrationLoader} />
            <Route path="candidates" element={<CandidatesListPage />} />
            <Route path="candidates-new" element={<NewRegistrationPage />} />
            <Route path="candidates/:candidateId" element={<CandidateDetailPage />} />
            <Route path="candidates-import-from-training" element={<ImportCandidatesFromTrainingPage />} />
            <Route path="candidates-import-from-excel-file" element={<ImportRegistrationsFromExcelFilePage />} />
            <Route path="export" element={<ExportRegistrationsPage />} />
            <Route path="payments" element={<ContractsListPage />} />
            <Route path="payments/stats" element={<ContractsStatsPage />} />
            <Route path="payments-settings" element={<ContractsPaymentsSettingsPage />} />
            <Route path="print-all-contracts" element={<PrintAllContractsPage />} />
            <Route path="contracts/:contractId" element={<ContractDetailPage />} />
          </Route>
          <Route path="members" element={<MembersListPage />} />
          <Route path="members/:memberId" element={<MemberDetailPage />} />
          <Route path="emails" element={<SchoolMailsPage />} />

        </Route>
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="family" element={<FamilySchoolPage />} loader={schoolLoader} />
        <Route path="family/training/:trainingId" element={<FamilyTrainingPage />} loader={schoolLoader} />
        <Route path="secretariat">
          <Route index element={<SecretariatHome />} loader={schoolLoader} />
          <Route path="classes" element={<SecretariatClassesListPage />} />
          <Route path="classes/:trainingId" element={<SecretariatClassesDetailPage />} />
          <Route path="classes/:trainingId/classe/:classeId" element={<SecretariatClasseDetailPage />} />
          <Route path="classes/:trainingId/classe/:classeId/print-teachers" element={<PrintClassePage />} />
          <Route path="classes/:trainingId/print-all-classes" element={<PrintAllClassesPage />} />
          <Route path="students" element={<SecretariatStudentsListPage />} />
          <Route path="students/:trainingId" element={<SecretariatStudentsDetailPage />} loader={trainingLoader} />
          <Route path="students/:trainingId/print-all-students" element={<PrintAllStudentsPage />} />
          <Route path="teachers" element={<SecretariatTeachersListPage />} />
          <Route path="teachers/:trainingId" element={<TeachersListPage />} loader={trainingLoader} />
          <Route path="teachers/:trainingId/print-all-teachers" element={<PrintAllTeachersPage />} />
          <Route path="presences" element={<PresencesListPage />} />
          <Route path="presences/:trainingId" element={<PresencesDetailPage />} />
          <Route path="presences/:trainingId/sessions/:sessionId" element={<PresencesSessionDetailPage />} />
          <Route path="payments" element={<SecretariatPaymentsListPage />} />
          <Route path="payments/:trainingId" element={<SecretariatPaymentsDetailPage />} />
          <Route path="payments/:trainingId/print-all-contracts" element={<PrintAllContractsPage />} />
          <Route path="payments/:trainingId/contracts/:contractId" element={<ContractDetailPage />} />
          <Route path="slot-change-requests" element={<SecretariatSlotChangeRequestsListPage />} />
          <Route path="slot-change-requests/:trainingId" element={<SecretariatSlotChangeRequestsDetailPage />} />
          <Route path="slot-change-requests/:trainingId/proposals" element={<SecretariatSlotChangeRequestsDetailPage />} />
          <Route path="agenda" element={<SecretariatAgendaListPage />} />
          <Route path="agenda/:trainingId" element={<SecretariatAgendaDetailPage />} />
        </Route>
        <Route path="teacher">
          <Route index element={<TeacherHome />} loader={schoolLoader} />
          <Route path="agenda" element={<TeacherAgendaPage />} loader={schoolLoader} />
          <Route path="agenda/:trainingId" element={<TeacherAgendaPage />} loader={schoolLoader} />
          <Route path="avis-passage" element={<TeacherTrainingsPage />} loader={schoolLoader} />
          <Route path="avis-passage/:trainingId" element={<TeacherClassesPage />} loader={schoolLoader} />
          <Route path="trainings/:trainingId/classes/:classId" element={<TeacherAvisPassagePage />} loader={schoolLoader} />
          <Route path="sessions" element={<TeacherSessionsTrainingsPage />} loader={schoolLoader} />
          <Route path="sessions/:trainingId" element={<TeacherSessionsPage />} loader={schoolLoader} />
          <Route path="classes" element={<TeacherClassesTrainingsPage />} loader={schoolLoader} />
          <Route path="classes/:trainingId" element={<TeacherClassesDetailPage />} loader={schoolLoader} />
          <Route path="classes/:trainingId/classe/:classeId" element={<TeacherClasseDetailPage />} loader={schoolLoader} />
          <Route path="classes/:trainingId/classe/:classeId/print-teachers" element={<PrintClassePage />} />
        </Route>
      </Route>
    </Route>
    <Route path="famille" element={<FamilyRequestAccessPage />} />
    <Route path="family/helloasso/return" element={<HelloAssoReturnPage />} />
    <Route path="family/helloasso/error" element={<HelloAssoErrorPage />} />
    <Route path="family/:registrationAccessKey" element={<FamilyPage />} />
    <Route path="family/:registrationAccessKey/:campaignId" element={<FamilyPage />} />
    <Route path="myspace/:registrationAccessKey" element={<FamilyPage />} />
    <Route path="myspace/:registrationAccessKey/:campaignId" element={<FamilyPage />} />
    <Route path="sys" element={<SystemLayout />}>
      <Route path="accounts" element={<SystemAccountsListPage />} />
      <Route path="schools" element={<SystemSchoolsListPage />} />
      <Route path="features" element={<SystemFeaturesListPage />} />
    </Route>
    {/* <Route path="*" element={<Navigate to="/" />} /> */}
  </Route>
);

const routes = createRoutesFromElements(RoutesJSX);
export const router = createBrowserRouter(routes, {
  future: {
    v7_fetcherPersist: true,
    v7_relativeSplatPath: true,
    v7_startTransition: true,
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <PrimeReactProvider value={configs}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </PrimeReactProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
